export const QUEST_TYPES = Object.freeze({
  STANDARD: "standard",
  DEADLINE: "deadline",
  DURATION: "duration",
});

const QUEST_CATEGORIES = new Set([
  "routine",
  "study",
  "fitness",
  "work",
  "recovery",
  "personal",
]);

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const trimText = (value, maxLength) => String(value || "").replace(/\s+/g, " ").trim().slice(0, maxLength);

const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
const twelveHourPattern = /^(\d{1,2}):(\d{2})\s*(am|pm)$/i;
const OVERNIGHT_DEADLINE_HOUR = 5;
const toMinutes = (hours, minutes) => hours * 60 + minutes;

function looksLikeWakeSleepQuestTitle(title) {
  const normalized = String(title || "").trim().toLowerCase();
  return normalized.startsWith("wake by ") || normalized.startsWith("sleep by ");
}

export function isAllDayVerificationQuest(quest) {
  if (!quest) return false;
  if (quest?.allDayVerification === true) return true;
  return looksLikeWakeSleepQuestTitle(quest?.title);
}

export function normalizeQuestType(value) {
  const next = String(value || "").trim().toLowerCase();
  if (next === QUEST_TYPES.DEADLINE || next === QUEST_TYPES.DURATION) {
    return next;
  }
  return QUEST_TYPES.STANDARD;
}

export function normalizeQuestCategory(value) {
  const next = String(value || "").trim().toLowerCase();
  return QUEST_CATEGORIES.has(next) ? next : "personal";
}

export function normalizeQuestDuration(value) {
  if (value === "" || value == null) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return clamp(Math.round(numeric), 15, 360);
}

export function normalizeQuestTime(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  if (timePattern.test(raw)) {
    return raw;
  }

  const twelveMatch = raw.match(twelveHourPattern);
  if (twelveMatch) {
    let hours = Number(twelveMatch[1]);
    const minutes = Number(twelveMatch[2]);
    const meridiem = twelveMatch[3].toLowerCase();

    if (hours === 12) {
      hours = meridiem === "am" ? 0 : 12;
    } else if (meridiem === "pm") {
      hours += 12;
    }

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }

  return null;
}

export function formatQuestTime(value) {
  const normalized = normalizeQuestTime(value);
  if (!normalized) return null;

  const [hours, minutes] = normalized.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatQuestDuration(value) {
  const minutes = normalizeQuestDuration(value);
  if (!minutes) return null;
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (!remainder) return `${hours}h`;
  return `${hours}h ${remainder}m`;
}

export function getQuestDeadlineLabel(quest) {
  const deadlineTime = normalizeQuestTime(quest?.deadlineTime);
  if (!deadlineTime) return null;
  if (isAllDayVerificationQuest(quest)) {
    return `Target ${formatQuestTime(deadlineTime)}`;
  }
  return `By ${formatQuestTime(deadlineTime)}`;
}

export function getQuestDeadlineDate(
  quest,
  questDateString = null,
  referenceDate = new Date()
) {
  const deadlineTime = normalizeQuestTime(quest?.deadlineTime);
  if (!deadlineTime) return null;

  const [hours, minutes] = deadlineTime.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }

  const anchorDate = questDateString
    ? new Date(`${questDateString}T00:00:00`)
    : new Date(referenceDate);

  anchorDate.setHours(0, 0, 0, 0);

  // Overnight cutoffs like 12:00 AM or 1:30 AM are treated as the end of the
  // current quest day, not an instant failure right after the daily board resets.
  if (hours < OVERNIGHT_DEADLINE_HOUR) {
    anchorDate.setDate(anchorDate.getDate() + 1);
  }

  anchorDate.setHours(hours, minutes, 0, 0);
  return anchorDate;
}

export function getQuestDurationLabel(quest) {
  const duration = normalizeQuestDuration(quest?.durationMinutes);
  if (!duration) return null;
  return `${formatQuestDuration(duration)} block`;
}

export function isDurationQuest(quest) {
  return normalizeQuestType(quest?.questType) === QUEST_TYPES.DURATION;
}

export function hasDurationQuestStarted(quest) {
  return !!String(quest?.startedAt || "").trim();
}

export function getDurationQuestTargetDate(quest) {
  const targetValue = String(quest?.targetEndAt || "").trim();
  if (targetValue) {
    const parsed = new Date(targetValue);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  const startedValue = String(quest?.startedAt || "").trim();
  const durationMinutes = normalizeQuestDuration(quest?.durationMinutes);
  if (!startedValue || !durationMinutes) return null;

  const startedAt = new Date(startedValue);
  if (Number.isNaN(startedAt.getTime())) return null;

  return new Date(startedAt.getTime() + durationMinutes * 60 * 1000);
}

export function getDurationQuestRemainingMs(quest, referenceDate = new Date()) {
  if (!isDurationQuest(quest) || !hasDurationQuestStarted(quest)) return null;
  const targetDate = getDurationQuestTargetDate(quest);
  if (!targetDate) return null;
  return Math.max(0, targetDate.getTime() - referenceDate.getTime());
}

export function isDurationQuestReadyToComplete(quest, referenceDate = new Date()) {
  if (!isDurationQuest(quest) || !hasDurationQuestStarted(quest)) return false;
  if (quest?.claimed || quest?.completed || quest?.status === "completed" || quest?.status === "failed") {
    return false;
  }
  const targetDate = getDurationQuestTargetDate(quest);
  if (!targetDate) return false;
  return targetDate.getTime() <= referenceDate.getTime();
}

export function formatCountdownDuration(diffMs) {
  if (!Number.isFinite(diffMs) || diffMs <= 0) return "00:00:00";

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

export function shouldDeferQuestDeadline(quest, wakeTime = null) {
  const deadlineTime = normalizeQuestTime(quest?.deadlineTime);
  if (!deadlineTime) return false;
  if (isAllDayVerificationQuest(quest)) return true;

  const [deadlineHours, deadlineMinutes] = deadlineTime.split(":").map(Number);
  if (!Number.isFinite(deadlineHours) || !Number.isFinite(deadlineMinutes)) {
    return false;
  }

  const normalizedWake = normalizeQuestTime(wakeTime);
  if (normalizedWake) {
    const [wakeHours, wakeMinutes] = normalizedWake.split(":").map(Number);
    if (Number.isFinite(wakeHours) && Number.isFinite(wakeMinutes)) {
      return toMinutes(deadlineHours, deadlineMinutes) <= toMinutes(wakeHours, wakeMinutes);
    }
  }

  return deadlineHours < OVERNIGHT_DEADLINE_HOUR;
}

export function getQuestCompletionText(quest) {
  const explicit = trimText(quest?.acceptanceCriteria, 110);
  if (explicit) return explicit;

  if (isAllDayVerificationQuest(quest)) {
    const targetLabel = formatQuestTime(quest?.deadlineTime);
    if (String(quest?.title || "").trim().toLowerCase().startsWith("wake by ")) {
      return `Stay honest. This remains claimable all day if you were up by ${targetLabel}.`;
    }
    if (String(quest?.title || "").trim().toLowerCase().startsWith("sleep by ")) {
      return `Stay honest. Claim this later only if you were in bed by ${targetLabel}.`;
    }
    return `Stay honest. This remains claimable all day if you met the target time.`;
  }

  const deadlineLabel = formatQuestTime(quest?.deadlineTime);
  if (normalizeQuestType(quest?.questType) === QUEST_TYPES.DEADLINE && deadlineLabel) {
    return `Complete it before ${deadlineLabel}.`;
  }

  const durationLabel = formatQuestDuration(quest?.durationMinutes);
  if (normalizeQuestType(quest?.questType) === QUEST_TYPES.DURATION && durationLabel) {
    return `Start the quest, hold the block for ${durationLabel}, then report completion.`;
  }

  return "";
}

export function isQuestExpired(
  quest,
  referenceDate = new Date(),
  questDateString = null,
  wakeTime = null
) {
  if (!quest) return false;
  if (quest.completed || quest.claimed || quest.status === "completed" || quest.status === "failed") {
    return false;
  }

  if (shouldDeferQuestDeadline(quest, wakeTime)) {
    return false;
  }

  const cutoff = getQuestDeadlineDate(quest, questDateString, referenceDate);
  if (!cutoff) return false;
  return referenceDate.getTime() > cutoff.getTime();
}

export function getUpcomingQuestDeadline(
  quests = [],
  referenceDate = new Date(),
  questDateString = null,
  wakeTime = null
) {
  const candidates = quests
    .filter((quest) =>
      quest &&
      !quest.completed &&
      !quest.claimed &&
      quest.status !== "failed" &&
      normalizeQuestTime(quest.deadlineTime) &&
      !shouldDeferQuestDeadline(quest, wakeTime)
    )
    .map((quest) => {
      const targetDate = getQuestDeadlineDate(quest, questDateString, referenceDate);
      return { quest, targetDate };
    })
    .filter(({ targetDate }) => targetDate && targetDate.getTime() > referenceDate.getTime())
    .sort((left, right) => left.targetDate.getTime() - right.targetDate.getTime());

  return candidates[0] || null;
}

function buildConstraintXP(level, baseXP) {
  const numericLevel = Math.max(1, Number(level) || 1);
  return clamp(baseXP + Math.round(numericLevel * 1.5), 20, 80);
}

function inferFocusLabel(goals = "", longTerm = "", nonNegotiables = "") {
  const combined = `${goals} ${longTerm} ${nonNegotiables}`.toLowerCase();
  if (/(study|exam|revision|course|assignment|lecture|learn)/.test(combined)) {
    return "study";
  }
  if (/(code|project|build|ship|client|work|product|design|feature)/.test(combined)) {
    return "deep work";
  }
  return "focus";
}

export function buildSystemConstraintQuests({
  wakeTime,
  sleepTime,
  focusMinutes,
  goals = "",
  longTerm = "",
  nonNegotiables = "",
  userLevel = 1,
} = {}) {
  const quests = [];

  const normalizedWake = normalizeQuestTime(wakeTime);
  if (normalizedWake) {
    const displayTime = formatQuestTime(normalizedWake);
    quests.push({
      title: `Wake by ${displayTime}`,
      desc: `Be fully out of bed and moving by ${displayTime}.`,
      xp: buildConstraintXP(userLevel, 24),
      penaltyQuest: "Prepare tomorrow morning before any entertainment tonight.",
      questType: QUEST_TYPES.DEADLINE,
      deadlineTime: normalizedWake,
      durationMinutes: null,
      category: "routine",
      allDayVerification: true,
      acceptanceCriteria: `Out of bed by ${displayTime}. No snooze resets.`,
    });
  }

  const normalizedSleep = normalizeQuestTime(sleepTime);
  if (normalizedSleep) {
    const displayTime = formatQuestTime(normalizedSleep);
    quests.push({
      title: `Sleep by ${displayTime}`,
      desc: `Lights out and in bed by ${displayTime}.`,
      xp: buildConstraintXP(userLevel, 24),
      penaltyQuest: "Set up tomorrow's shutdown routine before any late-night leisure.",
      questType: QUEST_TYPES.DEADLINE,
      deadlineTime: normalizedSleep,
      durationMinutes: null,
      category: "routine",
      allDayVerification: true,
      acceptanceCriteria: `In bed with lights out by ${displayTime}.`,
    });
  }

  const normalizedFocus = normalizeQuestDuration(focusMinutes);
  if (normalizedFocus) {
    const label = inferFocusLabel(goals, longTerm, nonNegotiables);
    const durationText = formatQuestDuration(normalizedFocus);
    quests.push({
      title: `Complete a ${durationText} ${label} block`,
      desc: `Protect one uninterrupted ${durationText} block for ${label}.`,
      xp: buildConstraintXP(userLevel, 30 + Math.round(normalizedFocus / 18)),
      penaltyQuest: "Do a 20-minute recovery review and reschedule the missed block.",
      questType: QUEST_TYPES.DURATION,
      deadlineTime: null,
      durationMinutes: normalizedFocus,
      category: label === "study" ? "study" : "work",
      acceptanceCriteria: `Log ${durationText} of uninterrupted ${label} today.`,
    });
  }

  return quests;
}

export function normalizeGeneratedQuest(rawQuest, index = 0) {
  const questType = normalizeQuestType(rawQuest?.questType);
  const durationMinutes = normalizeQuestDuration(rawQuest?.durationMinutes);
  const deadlineTime = normalizeQuestTime(rawQuest?.deadlineTime);
  const sanitizedType =
    questType === QUEST_TYPES.DEADLINE && !deadlineTime
      ? QUEST_TYPES.STANDARD
      : questType === QUEST_TYPES.DURATION && !durationMinutes
        ? QUEST_TYPES.STANDARD
        : questType;

  const titleFallback =
    sanitizedType === QUEST_TYPES.DEADLINE && deadlineTime
      ? `Complete by ${formatQuestTime(deadlineTime)}`
      : sanitizedType === QUEST_TYPES.DURATION && durationMinutes
        ? `Complete a ${formatQuestDuration(durationMinutes)} block`
        : `System quest ${index + 1}`;

  const descFallback =
    sanitizedType === QUEST_TYPES.DEADLINE && deadlineTime
      ? `Finish this objective before ${formatQuestTime(deadlineTime)}.`
      : sanitizedType === QUEST_TYPES.DURATION && durationMinutes
        ? `Log ${formatQuestDuration(durationMinutes)} of focused work today.`
        : "Complete one specific objective today.";

  return {
    title: trimText(rawQuest?.title, 60) || titleFallback,
    desc: trimText(rawQuest?.desc, 120) || descFallback,
    xp: clamp(Number(rawQuest?.xp) || 20, 15, 90),
    penaltyQuest: trimText(rawQuest?.penaltyQuest, 110),
    questType: sanitizedType,
    deadlineTime: sanitizedType === QUEST_TYPES.DEADLINE ? deadlineTime : deadlineTime,
    durationMinutes: sanitizedType === QUEST_TYPES.DURATION ? durationMinutes : null,
    category: normalizeQuestCategory(rawQuest?.category),
    acceptanceCriteria: trimText(rawQuest?.acceptanceCriteria, 110),
    allDayVerification: rawQuest?.allDayVerification === true,
    fixed: false,
    status: "pending",
    claimed: false,
    penaltyStatus: "pending",
    isPenalty: false,
  };
}

export function dedupeQuests(quests = []) {
  const seen = new Set();
  return quests.filter((quest) => {
    const key = trimText(quest?.title, 80).toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
